// create store / configure redux store

import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunk from 'redux-thunk';
import storageSession from 'redux-persist/lib/storage/session';
import { persistStore, persistReducer } from 'redux-persist';

import reducer from './../reducers';

// FUDR-13759: tightened persist config — switched from blacklist to whitelist.
//
// Root whitelist (this file): only 'authentication' — session identity tokens
// (loginData, authData, etc.); needed on every cold start to skip the sign-in flow.
//
// Slice-level persistors (reducers/index.js):
//   'app'      — blacklist keeps session-essential keys (cart, selectedTable, …);
//                large API-fetched keys are not persisted.
//   'common'   — whitelist themeData + resources (~7 KB) so useTheme() can run on cold
//                start; __FUDR_BOOT__ hydrate runs after rehydrate and replaces them.
//   'ordering' — whitelist cart/session keys only.
//   'rewards'  — whitelist rewardGuestDetails only (logged-in guest profile).
//
// Previous blacklist: ['loginData'] — this was a no-op because loginData is nested
// inside the 'authentication' slice, not a root-level key.
const PERSIST_VERSION = 1;

// redux-persist does not purge on version bump without migrate; stale root-level
// app/common/ordering/rewards blobs can crash rehydrate against slice-level persistors.
const migratePersistedState = (state, currentVersion) => {
  const inboundVersion =
    state && state._persist && state._persist.version !== undefined
      ? state._persist.version
      : -1;

  if (inboundVersion === currentVersion) {
    return Promise.resolve(state);
  }

  const authentication = state && state.authentication;
  return Promise.resolve(authentication ? { authentication } : {});
};

const persistConfig = {
  // Keep key 'app' (same as the pre-FUDR-13759 key) so existing sessions can still
  // load their 'authentication' slice on first deploy — users won't need to
  // re-authenticate. Root whitelist is only 'authentication'; app/common/ordering/rewards use
  // slice-level persistors in reducers/index.js. Stale root-level rewards/ordering keys
  // in old persisted state are silently ignored by redux-persist.
  key: 'app',
  storage: storageSession,
  // Persist only root slices that aren't already persisted at slice-level.
  // `app`, `common`, `ordering`, and `rewards` use slice-level persistors in reducers/index.js.
  whitelist: ['authentication'],
  // Purge old root-level persisted state (schema changed: slice-level persistors now own app/common/ordering).
  version: PERSIST_VERSION,
  migrate: migratePersistedState,
};

const persistedReducer = persistReducer(persistConfig, reducer);

const configureStore = () => {
  const store = createStore(persistedReducer, composeWithDevTools(applyMiddleware(thunk)));
  const persistor = persistStore(store);
  return {
    store,
    persistor,
  };
};

export default configureStore();
